import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import KudosButton from '../components/KudosButton';
import {
 BookOpen, Bot, Calculator, Video, Users,
 ArrowRight, ArrowUpRight, CheckCircle2, ShieldCheck, TrendingUp,
 Calendar, Megaphone, Sparkles, Bell, Youtube, Instagram, Facebook,
 MessageSquare, Mail, Smartphone, GraduationCap, Award,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdDisplay from '../components/AdDisplay';
import BrandMark from '../components/BrandMark';
import WelcomeVideoPopup from '../components/WelcomeVideoPopup';
import HeroVideoBlock from '../components/HeroVideoBlock';
import TopicHubGrid from '../components/TopicHubGrid';
import CountryConfirmDialog, { getSavedCountry } from '../components/CountryConfirmDialog';
import { CartButton } from '../components/CartDrawer';

const features = [
 { icon: BookOpen, title: 'Learning & Courses', desc: 'Structured lessons written by working operators — no fluff, no filler.', slug: 'learning' },
 { icon: Bot, title: 'AI Assistant', desc: 'Ask specific freeze-dry questions and get grounded, cited answers.', slug: 'ai-assistant' },
 { icon: Calculator, title: 'Business Tools', desc: 'Real batch math: margin, capacity, break-even and sensitivity.', slug: 'business-tools' },
 { icon: Video, title: 'Video Library', desc: 'Curated tutorials focused on the things new operators get stuck on.', slug: 'videos' },
 { icon: Users, title: 'Community Forum', desc: 'Talk shop with operators who actually run the same machines you do.', slug: 'community' },
];

const capabilities = [
 { label: 'Built for operators', desc: 'Not for tourists or dropshippers.' },
 { label: 'Real batch math', desc: 'Calculators grounded in cycle behaviour.' },
 { label: 'AI you can trust', desc: 'Answers cited from freeze-dry science.' },
 { label: 'Installable app', desc: 'Add to your home screen on desktop or mobile.' },
];

const benefits = [
 'Practical curriculum, not marketing',
 'AI assistant tuned to freeze-drying',
 'Batch, margin and capacity calculators',
 'Vendor and consultant directory',
 'Installable app — desktop + mobile',
 'Optional 1:1 consulting when you need it',
];

// Static (for now) curated feed — swap to /api/public/announcements later.
const upcomingEvents = [
 {
  kind: 'webinar',
  date: 'Feb 22',
  title: 'Cycle tuning for meats: pork, beef & poultry',
  body: 'Live 45-min session with Q&A. Free for all members.',
  cta: 'Reserve seat',
  to: '/login?mode=signup',
 },
 {
  kind: 'course drop',
  date: 'Feb 26',
  title: 'New Pro course: Multi-machine scheduling',
  body: 'Two-hour deep dive on running 4+ freeze dryers in parallel.',
  cta: 'Preview curriculum',
  to: '/platform/learning',
 },
 {
  kind: 'community',
  date: 'Mar 04',
  title: 'Community meet-up · Colorado Springs',
  body: 'In-person meet-up for local operators. RSVP in the forum.',
  cta: 'See in forum',
  to: '/platform/community',
 },
];

const recentAnnouncements = [
 { title: 'Web push notifications are live', body: 'Enable them from your notifications inbox to get replies + new lessons on your phone.', when: '3 days ago' },
 { title: 'Business Calculator now exports PDF', body: 'One-click pro-forma PDF for lenders and co-founders.', when: '1 week ago' },
 { title: 'Vendor directory added 12 new suppliers', body: 'Mylar, oxygen absorbers, custom labels — all vetted.', when: '2 weeks ago' },
];

const socialLinks = [
 { icon: Youtube,        label: 'YouTube',   href: '#', color: 'hover:text-[#FF0000]' },
 { icon: Instagram,      label: 'Instagram', href: '#', color: 'hover:text-[#E1306C]' },
 { icon: Facebook,       label: 'Facebook',  href: '#', color: 'hover:text-[#1877F2]' },
 { icon: MessageSquare,  label: 'TikTok',    href: '#', color: 'hover:text-foreground' },
];

const LandingPage = () => {
 const navigate = useNavigate();
 const { login, API } = useContext(AuthContext);
 const [showAuth, setShowAuth] = useState(false);
 const [authMode, setAuthMode] = useState('login');
 const [email, setEmail] = useState('');
 const [password, setPassword] = useState('');
 const [loading, setLoading] = useState(false);
 const [ageConfirmed, setAgeConfirmed] = useState(false);
 const [events, setEvents] = useState([]);
 const [announcements, setAnnouncements] = useState([]);
 const [graduates, setGraduates] = useState([]);
 const [ambassadors, setAmbassadors] = useState([]);
 const [countryOpen, setCountryOpen] = useState(false);
 const [pendingEvent, setPendingEvent] = useState(null);
 // Referral preview — validate a stored personal ref code so the signup
 // card can display "Invited by <name>" the same way certificate refs do.
 const [refPreview, setRefPreview] = useState(null); // { name, code }

 // Capture referral from URL (?ref=…) — persisted so it survives auth flow.
 // FDA-* / FDACADEMY-* → certificate ref; anything else → personal user code
 // (e.g. TEST-P6ZJ). Each flavour lands in its own localStorage key so the
 // register payload picks the right field (referral_cert vs referral_code).
 useEffect(() => {
   try {
     const params = new URLSearchParams(window.location.search);
     const ref = (params.get('ref') || '').trim();
     if (!ref) return;
     if (/^(FDA|FDACADEMY)-/i.test(ref)) {
       window.localStorage.setItem('fda_ref_cert', ref.toUpperCase());
     } else {
       window.localStorage.setItem('fda_ref_code', ref.toUpperCase());
     }
   } catch (_) { /* ignore */ }
 }, []);

 // Validate any stored personal referral code so we can render an
 // "Invited by …" banner on the signup card. Silently no-ops for cert refs
 // (those don't have a personal name attached).
 useEffect(() => {
   let alive = true;
   (async () => {
     try {
       const code = (window.localStorage.getItem('fda_ref_code') || '').trim().toUpperCase();
       if (!code) return;
       const { data } = await axios.get(`${API}/referrals/validate/${encodeURIComponent(code)}`);
       if (alive && data?.valid) setRefPreview({ name: data.referrer_name, code });
     } catch (_) { /* ignore */ }
   })();
   return () => { alive = false; };
 }, [API]);

 // Fetch admin-managed events; fall back to the static array below if the
 // backend returns nothing (fresh install, no events created yet).
 useEffect(() => {
  let alive = true;
  (async () => {
    try {
      const { data } = await axios.get(`${API}/public/events`);
      if (alive) setEvents(data?.items || []);
    } catch (_) { /* ignore, we'll fall back */ }
  })();
  return () => { alive = false; };
 }, [API]);

 // Fetch top ambassadors (last 30 days) for the social-proof strip.
 useEffect(() => {
  let alive = true;
  (async () => {
    try {
      const { data } = await axios.get(`${API}/referrals/leaderboard?limit=6&window_days=30`);
      if (alive) setAmbassadors(data?.entries || []);
    } catch (_) { /* ignore — strip silently hides */ }
  })();
  return () => { alive = false; };
 }, [API]);

 // Fetch dedicated What's New items first; fall back to admin broadcasts
 // so the "What just shipped" strip never goes blank on a fresh install.
 useEffect(() => {
  let alive = true;
  (async () => {
    try {
      const { data: wn } = await axios.get(`${API}/public/whats-new?limit=6`);
      if (alive && Array.isArray(wn?.items) && wn.items.length > 0) {
        setAnnouncements(wn.items);
        return;
      }
    } catch (_) { /* fall through to broadcasts */ }
    try {
      const { data } = await axios.get(`${API}/public/announcements?limit=6`);
      if (alive) setAnnouncements(data?.items || []);
    } catch (_) { /* ignore, fall back to static */ }
  })();
  return () => { alive = false; };
 }, [API]);

 // Fetch the latest 8 graduates for the social-proof strip.
 useEffect(() => {
  let alive = true;
  (async () => {
    try {
      const { data } = await axios.get(`${API}/graduates?limit=8`);
      if (alive) setGraduates(data?.graduates || []);
    } catch (_) { /* ignore */ }
  })();
  return () => { alive = false; };
 }, [API]);

 const eventsList = events.length ? events : upcomingEvents;
 const announcementsList = announcements.length
   ? announcements.map((a) => ({
       title: a.title,
       body: a.body,
       when: (() => {
         try {
           const d = new Date(a.created_at);
           const days = Math.floor((Date.now() - d.getTime()) / 86400000);
           if (days === 0) return 'today';
           if (days === 1) return 'yesterday';
           if (days < 7) return `${days} days ago`;
           if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
           return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
         } catch (_) {
           return '';
         }
       })(),
     }))
   : recentAnnouncements;

 const handleEventClick = (ev) => {
   setPendingEvent(ev);
   if (getSavedCountry()) {
     // Already have their country — record the RSVP silently and navigate.
     recordRsvp(ev, getSavedCountry());
   } else {
     setCountryOpen(true);
   }
 };

 const recordRsvp = async (ev, country) => {
   try {
     if (ev.id) {
       await axios.post(`${API}/public/events/${ev.id}/rsvp`, {
         event_id: ev.id, country, referer: typeof window !== 'undefined' ? window.location.pathname : null,
       });
     }
   } catch (_) { /* ignore telemetry failures */ }
   navigate(ev.cta_link || ev.to || '/login?mode=signup');
 };

 const handleAuth = async (e) => {
 e.preventDefault();
 if (authMode === 'register' && !ageConfirmed) {
   toast.error('Please tick the box to confirm you are 18+ and accept the Terms of Use.');
   return;
 }
 setLoading(true);
 try {
 const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
 const payload = { email, password };
 if (authMode === 'register') {
   payload.age_confirmed = true;
   payload.accepted_terms = true;
   payload.accepted_terms_version = '2026-02-canada-v1';
   try {
     const stored = window.localStorage.getItem('fda_ref_cert');
     if (stored) payload.referral_cert = stored;
     const storedCode = window.localStorage.getItem('fda_ref_code');
     if (storedCode) payload.referral_code = storedCode;
   } catch (_) { /* ignore */ }
 }
 const res = await axios.post(`${API}${endpoint}`, payload);
 // Successful signup — clear the stored ref so it doesn't leak into future logins.
 if (authMode === 'register') {
   try {
     window.localStorage.removeItem('fda_ref_cert');
     window.localStorage.removeItem('fda_ref_code');
   } catch (_) { /* ignore */ }
 }
 login(res.data.access_token, res.data.user);
 toast.success(`Welcome ${authMode === 'login' ? 'back' : 'to Freeze Dry Academy'}!`);
 // Route new signups through the Pricing gateway (free-to-start card,
 // access-code + creator-code redemption) before dropping them in-app.
 if (authMode === 'register') {
   navigate('/freedriq?tier=free&phase=pre&from=signup');
 }
 } catch (error) {
 toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Authentication failed');
 } finally {
 setLoading(false);
 }
 };

 if (showAuth) {
 return (
 <div className="min-h-screen bg-navy-spotlight flex items-center justify-center p-4">
 <Card className="pro-card w-full max-w-md animate-fade-up" data-testid="auth-card">
 <CardHeader className="space-y-3 text-center pb-4">
 <span className="eyebrow mx-auto" data-testid="auth-eyebrow">Members</span>
 <CardTitle className="font-heading text-3xl tracking-tight">
 {authMode === 'login' ? 'Welcome back' : 'Create your account'}
 </CardTitle>
 <CardDescription>
 {authMode === 'login'
 ? 'Sign in to continue your freeze-drying education.'
 : 'Start with the free tier — upgrade anytime.'}
 </CardDescription>
 </CardHeader>
 <CardContent>
 <Tabs value={authMode} onValueChange={setAuthMode}>
 <TabsList className="grid w-full grid-cols-2 mb-5" data-testid="auth-tabs">
 <TabsTrigger value="login" data-testid="login-tab">Sign in</TabsTrigger>
 <TabsTrigger value="register" data-testid="register-tab">Sign up</TabsTrigger>
 </TabsList>
 <form onSubmit={handleAuth} className="space-y-4">
{authMode === 'register' && refPreview && (
  <div
    className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm flex items-start gap-2"
    data-testid="ref-preview-banner"
  >
    <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
    <div className="leading-snug">
      <div className="font-bold text-foreground">Invited by {refPreview.name}</div>
      <div className="text-muted-foreground text-xs mt-0.5">
        Code <span className="font-mono font-bold">{refPreview.code}</span> will be applied when you finish signing up.
      </div>
    </div>
  </div>
)}
 <div className="space-y-1.5">
 <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
 <Input
 id="email" type="email" placeholder="you@example.com"
 value={email} onChange={(e) => setEmail(e.target.value)} required
 data-testid="email-input" className="h-11"
 />
 </div>
 <div className="space-y-1.5">
 <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</Label>
 <Input
 id="password" type="password" placeholder="••••••••"
 value={password} onChange={(e) => setPassword(e.target.value)} required
 data-testid="password-input" className="h-11"
 />
 </div>
 {authMode === 'register' && (
 <label className="relative z-10 flex items-start gap-2.5 text-xs cursor-pointer rounded-lg border border-border p-3 bg-background hover:border-primary/60 transition-colors" data-testid="age-confirm-label">
 <input
 type="checkbox"
 checked={ageConfirmed}
 onChange={(e) => setAgeConfirmed(e.target.checked)}
 className="mt-0.5 h-4 w-4 rounded border-input flex-shrink-0"
 data-testid="age-confirm-checkbox"
 required
 />
 <span className="text-muted-foreground leading-relaxed">
 I confirm I am <strong className="text-foreground">18 or older</strong> and I agree to the{' '}
 <a href="/terms" target="_blank" rel="noopener" className="underline font-bold text-foreground">Terms of Use</a>
 {' '}— including the <strong className="text-foreground">anti-piracy prosecution</strong> and <strong className="text-foreground">Canadian jurisdiction</strong> clauses.
 </span>
 </label>
 )}
 <Button type="submit" size="lg" className="w-full h-11 font-bold" disabled={loading} data-testid="auth-submit-btn">
 {loading ? 'Please wait…' : authMode === 'login' ? 'Sign in' : 'Create account'}
 {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
 </Button>
 </form>
 </Tabs>
 <Button variant="ghost" className="w-full mt-4 text-muted-foreground" onClick={() => setShowAuth(false)} data-testid="back-to-landing-btn">
 ← Back to home
 </Button>
 </CardContent>
 </Card>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-background" data-testid="landing-page">
 <WelcomeVideoPopup
 storageKey="fda_welcome_video_landing"
 />
 <CountryConfirmDialog
 open={countryOpen}
 onOpenChange={setCountryOpen}
 eventTitle={pendingEvent?.title}
 onConfirm={(country) => { if (pendingEvent) recordRsvp(pendingEvent, country); }}
 />
 {/* Utility top bar */}
 <div className="bg-navy text-background/80 text-xs">
 <div className="container mx-auto px-6 h-9 flex items-center justify-center gap-6">
 <span className="flex items-center gap-1.5">
 <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Built for operators — not marketing
 </span>
 <span className="hidden sm:inline opacity-60">·</span>
 <span className="hidden sm:flex items-center gap-1.5">
 <Smartphone className="w-3.5 h-3.5 text-primary" /> Installable on desktop and mobile
 </span>
 </div>
 </div>

 {/* Main nav */}
 <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
 <div className="container mx-auto px-6 h-16 flex items-center justify-between">
 <div className="flex items-center gap-2.5" data-testid="brand-mark">
 <BrandMark size="lg" subtitle="Learning · Tools · Community" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
 </div>
 <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-foreground/80">
 <a href="#benefits" className="hover:text-foreground transition">Platform</a>
 <button onClick={() => navigate('/platform')} className="hover:text-foreground transition" data-testid="nav-all-tools">All tools</button>
 <button onClick={() => navigate('/graduates')} className="hover:text-foreground transition" data-testid="nav-graduates">Graduates</button>
 <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition">Pricing</button>
 <button onClick={() => navigate('/why-us')} className="hover:text-foreground transition" data-testid="nav-why-us">Why us</button>
 <button onClick={() => navigate('/shop')} className="hover:text-foreground transition" data-testid="nav-shop">Shop</button>
 </nav>
 <div className="flex items-center gap-2">
 <CartButton />
 <Button variant="ghost" size="sm" onClick={() => navigate('/login')} className="font-semibold" data-testid="nav-login-btn">Log in</Button>
 <Button
 size="sm"
 className="rounded-full px-5 font-bold bg-primary text-primary-foreground hover:bg-primary/90"
 onClick={() => { setAuthMode('register'); setShowAuth(true); }}
 data-testid="nav-signup-btn"
 >
 Get started <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" />
 </Button>
 </div>
 </div>
 </header>

 {/* HERO — dark navy spotlight */}
 <section className="bg-navy-spotlight text-background relative overflow-hidden">
 <div className="container mx-auto px-6 pt-16 pb-24 text-center relative z-10">
 <span className="pill-badge animate-fade-up" data-testid="hero-eyebrow">
 <Sparkles className="w-3.5 h-3.5 fill-primary-foreground" /> A workbench for freeze-dry operators
 </span>
 <h1 className="font-heading mt-7 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-balance animate-fade-up" style={{ '--i': 1 }}>
 The tools and community
 <br />
 <span className="text-amber-brand">real freeze-dry operators need.</span>
 </h1>
 <p className="mt-6 max-w-2xl mx-auto text-lg text-background/75 leading-relaxed animate-fade-up" style={{ '--i': 2 }}>
 Calculators built for real batch math. AI trained on freeze-dry science. A community
 for actual operators.
 </p>
 <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 animate-fade-up" id="start" style={{ '--i': 3 }}>
 <Button
 size="lg"
 className="h-12 px-7 rounded-full font-bold text-base bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_8px_24px_-8px_hsl(43_100%_48%/0.5)]"
 onClick={() => { setAuthMode('register'); setShowAuth(true); }}
 data-testid="start-free-btn"
 >
 Start free <ArrowRight className="ml-2 w-4 h-4" />
 </Button>
 <Button
 size="lg"
 variant="outline"
 className="h-12 px-7 rounded-full font-bold text-base border-background/40 text-background bg-transparent hover:bg-background/10 hover:text-background"
 onClick={() => navigate('/login')}
 data-testid="login-btn"
 >
 I already have an account
 </Button>
 </div>

 {/* Capability chips (replaces claims-based stats row) */}
 <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl mx-auto animate-fade-up" style={{ '--i': 4 }}>
 {capabilities.map((c) => (
 <div key={c.label} className="text-left border border-background/15 rounded-2xl p-4 bg-background/5 backdrop-blur">
 <div className="font-heading font-bold text-background text-base">{c.label}</div>
 <div className="text-xs text-background/60 mt-1">{c.desc}</div>
 </div>
 ))}
 </div>
 </div>

 {/* subtle bottom fade to next section */}
 <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-background/10 pointer-events-none" />
 </section>

 {/* LATEST GRADUATES STRIP — social proof from real credentials */}
 {graduates.length > 0 && (
   <section className="container mx-auto px-6 py-10" data-testid="landing-graduates-strip">
     <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
       <div>
         <span className="eyebrow inline-flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5" /> Latest graduates</span>
         <h3 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">
           Real operators. <span className="text-amber-brand">Real credentials.</span>
         </h3>
       </div>
       <Button
         variant="ghost"
         size="sm"
         onClick={() => navigate('/graduates')}
         className="rounded-full font-semibold"
         data-testid="landing-graduates-view-all"
       >
         See the wall <ArrowRight className="w-3.5 h-3.5 ml-1" />
       </Button>
     </div>
     <div className="flex items-stretch gap-3 overflow-x-auto no-scrollbar pb-2" role="list">
       {graduates.map((g) => (
         <div
           key={g.cert_number}
           role="listitem"
           className="flex-shrink-0 w-64 rounded-2xl overflow-hidden bg-card border border-border hover:border-primary/60 transition-all hover:-translate-y-0.5 hover:shadow-md"
         >
           <button
             type="button"
             onClick={() => navigate(`/certificate/${g.cert_number}`)}
             className="text-left w-full"
             data-testid={`landing-graduate-${g.cert_number}`}
           >
             <div className="aspect-[1200/630] bg-navy-spotlight">
               <img
                 src={`${API}/verify/${g.cert_number}/share-card.png`}
                 alt={`${g.first_name} ${g.last_initial || ''}`.trim()}
                 className="w-full h-full object-cover"
                 loading="lazy"
                 onError={(e) => { e.currentTarget.style.opacity = '0.15'; }}
               />
             </div>
             <div className="p-3">
               <div className="font-heading font-bold text-sm text-foreground truncate">
                 {g.first_name}{g.last_initial ? ` ${g.last_initial}` : ''}
               </div>
               <div className="text-[11px] text-muted-foreground truncate">{g.title}</div>
             </div>
           </button>
           <div className="px-3 pb-3 -mt-1 flex items-center justify-between">
             <KudosButton
               targetType="graduate_cert"
               targetId={g.cert_number}
               size="sm"
             />
             <span className="text-[10px] text-muted-foreground font-semibold">Cheer</span>
           </div>
         </div>
       ))}
     </div>
   </section>
 )}

 <div className="max-w-4xl mx-auto px-6 pt-10 pb-10" data-testid="landing-hero-ad-wrap">
 <AdDisplay location="landing_hero" className="flex justify-center" />
 </div>

{/* HERO EXPLAINER VIDEO — admin-controlled via /admin/ad-slots hero-video card */}
 <HeroVideoBlock />

 {/* FEATURES */}
 <section id="features" className="container mx-auto px-6 py-24">
 <div className="max-w-2xl mb-12">
 <span className="eyebrow">Platform</span>
 <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">
 Everything you need to <span className="text-amber-brand">run better batches.</span>
 </h2>
 <p className="mt-4 text-muted-foreground text-lg">
 Built like the gear it teaches — durable, simple, and built for daily use.
 </p>
 </div>
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
 {features.map((f, i) => {
 const Icon = f.icon;
 return (
 <button
 key={f.title}
 onClick={() => navigate(`/platform/${f.slug}`)}
 className="pro-card-interactive text-left p-7 animate-fade-up group"
 style={{ '--i': i }}
 data-testid={`feature-card-${i}`}
 >
 <div className="flex items-start justify-between">
 <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
 <Icon className="w-5 h-5" strokeWidth={2} />
 </div>
 <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition" />
 </div>
 <h3 className="mt-5 font-heading text-xl font-bold tracking-tight">{f.title}</h3>
 <p className="mt-2 text-muted-foreground leading-relaxed text-[15px]">{f.desc}</p>
 <div className="mt-4 text-[11px] font-extrabold text-primary uppercase tracking-widest">
 Learn more →
 </div>
 </button>
 );
 })}
 </div>
 <div className="mt-8 flex justify-center">
 <Button
 variant="outline"
 onClick={() => navigate('/platform')}
 className="rounded-full font-semibold px-6"
 data-testid="features-all-tools-btn"
 >
 See every tool in one place
 <ArrowRight className="w-4 h-4 ml-2" />
 </Button>
 </div>
 </section>

 <div className="max-w-4xl mx-auto px-6 py-10">
 <AdDisplay location="landing_ad_slot_1" className="flex justify-center" />
 </div>

 {/* TOPIC HUB GRID — SEO discovery pillar; renders every /topics/:slug */}
 <TopicHubGrid />

 {/* TOP AMBASSADORS — social proof strip powered by /referrals/leaderboard.
     Silently hides when nobody has crossed a milestone in the window. */}
 {ambassadors.length > 0 && (
   <section className="container mx-auto px-6 py-10" data-testid="landing-ambassadors-strip">
     <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
       <div>
         <span className="eyebrow inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> Top ambassadors this month</span>
         <h3 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">
           Members are bringing <span className="text-amber-brand">real operators</span> in.
         </h3>
       </div>
       <div className="text-xs text-muted-foreground">
         Ranked by signups in the last 30 days.
       </div>
     </div>
     <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" role="list">
       {ambassadors.map((a) => (
         <div
           key={`${a.rank}-${a.display_name}`}
           role="listitem"
           className="rounded-2xl bg-card border border-border p-4 flex flex-col items-center text-center hover:border-primary/60 transition-all hover:-translate-y-0.5"
           data-testid={`ambassador-${a.rank}`}
         >
           <div className="relative">
             {a.avatar_url ? (
               <img src={a.avatar_url} alt={a.display_name} className="w-12 h-12 rounded-full object-cover border-2 border-border" />
             ) : (
               <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-lg">
                 {(a.display_name || '?').charAt(0)}
               </div>
             )}
             <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[9px] font-black rounded-full w-5 h-5 flex items-center justify-center">
               {a.rank}
             </span>
           </div>
           <div className="mt-2 font-heading text-sm font-bold tracking-tight truncate w-full">{a.display_name}</div>
           {a.milestone_tier && (
             <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-500/40 px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold">
               <Award className="w-2.5 h-2.5" /> {a.milestone_tier.label}
             </div>
           )}
           <div className="mt-1 text-[11px] text-muted-foreground">
             <span className="font-bold text-foreground">{a.direct_referrals}</span> signup{a.direct_referrals === 1 ? '' : 's'}
           </div>
         </div>
       ))}
     </div>
   </section>
 )}

 {/* BENEFITS — navy CTA block (HomeLyph style) */}
 <section id="benefits" className="container mx-auto px-6 py-16">
 <div className="bg-navy-gradient text-background rounded-3xl overflow-hidden relative">
 <div className="grid lg:grid-cols-2 gap-10 p-10 lg:p-16">
 <div>
 <span className="pill-badge"><TrendingUp className="w-3.5 h-3.5" /> Why us</span>
 <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-5">
 Built by operators.
 <br />
 <span className="text-amber-brand">For the next ones.</span>
 </h2>
 <p className="mt-5 text-background/70 leading-relaxed text-[15px] max-w-md">
 Everything we wish we&apos;d had on day one — without the noise, the upsells, or the spam.
 </p>
 <Button
 size="lg"
 className="mt-8 rounded-full h-12 px-7 font-bold bg-primary text-primary-foreground hover:bg-primary/90"
 onClick={() => { setAuthMode('register'); setShowAuth(true); }}
 data-testid="get-started-btn"
 >
 Get started free <ArrowRight className="ml-2 w-4 h-4" />
 </Button>
 </div>
 <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
 {benefits.map((b) => (
 <li key={b} className="flex items-start gap-3 text-background/90">
 <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
 <span className="text-[15px]">{b}</span>
 </li>
 ))}
 </ul>
 </div>
 </div>
 </section>

 <div className="max-w-4xl mx-auto px-6 py-10">
 <AdDisplay location="landing_ad_slot_2" className="flex justify-center" />
 </div>

 {/* UPCOMING EVENTS + RECENT ANNOUNCEMENTS */}
 <section id="events" className="container mx-auto px-6 py-20" data-testid="landing-events">
 <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
 {/* Upcoming events */}
 <div>
 <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
 <div>
 <span className="pill-badge"><Calendar className="w-3.5 h-3.5" /> Upcoming events</span>
 <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">
 What&apos;s coming up.
 </h2>
 </div>
 <Button
 variant="outline"
 className="rounded-full font-semibold hidden sm:inline-flex"
 onClick={() => navigate('/events')}
 data-testid="events-view-all"
 >
 View full calendar <ArrowRight className="w-4 h-4 ml-2" />
 </Button>
 </div>
 <ul className="space-y-3" data-testid="events-list">
 {upcomingEvents.map((ev, i) => (
 <li
 key={i}
 className="pro-card p-5 flex items-start gap-4 hover:border-primary/60 transition-colors group animate-fade-up"
 style={{ '--i': i }}
 data-testid={`event-${i}`}
 >
 <div className="w-16 flex-shrink-0 text-center rounded-lg bg-primary/10 text-primary py-2">
 <div className="text-[10px] uppercase tracking-widest font-extrabold">{ev.date.split(' ')[0]}</div>
 <div className="font-heading text-2xl font-extrabold leading-none">{ev.date.split(' ')[1]}</div>
 </div>
 <div className="min-w-0 flex-1">
 <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-primary font-extrabold">
 <Sparkles className="w-3 h-3" /> {ev.kind}
 </div>
 <h3 className="mt-1 font-heading text-lg font-bold tracking-tight">{ev.title}</h3>
 <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{ev.body}</p>
 </div>
 <button
 type="button"
 onClick={() => navigate(ev.to)}
 className="hidden md:inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors flex-shrink-0 self-center"
 data-testid={`event-cta-${i}`}
 >
 {ev.cta} <ArrowRight className="w-3 h-3" />
 </button>
 </li>
 ))}
 </ul>
 </div>

 {/* Recent announcements */}
 <div>
 <span className="pill-badge"><Megaphone className="w-3.5 h-3.5" /> Latest announcements</span>
 <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">
 What just shipped.
 </h2>
 <ul className="mt-6 space-y-3" data-testid="announcements-list">
 {announcementsList.map((a, i) => (
 <li
 key={i}
 className="pro-card p-4 animate-fade-up"
 style={{ '--i': i }}
 data-testid={`announcement-${i}`}
 >
 <div className="flex items-start gap-3">
 <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
 <Bell className="w-4 h-4" />
 </div>
 <div className="min-w-0">
 <div className="font-heading text-sm font-extrabold tracking-tight">{a.title}</div>
 <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{a.body}</p>
 <div className="text-[10px] uppercase tracking-widest text-muted-foreground/80 mt-2">{a.when}</div>
 </div>
 </div>
 </li>
 ))}
 </ul>
 <div className="mt-4">
 <Button
 variant="ghost"
 size="sm"
 className="w-full rounded-full font-semibold text-muted-foreground"
 onClick={() => { setAuthMode('register'); setShowAuth(true); }}
 data-testid="announcements-signup"
 >
 <Bell className="w-3.5 h-3.5 mr-1.5" /> Sign up to get these in your inbox
 </Button>
 </div>
 </div>
 </div>
 </section>

 {/* WE'RE SOCIAL — join the discussion online */}
 <section id="social" className="container mx-auto px-6 pb-20" data-testid="landing-social">
 <div className="bg-navy-gradient text-background rounded-3xl overflow-hidden relative">
 <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(245,181,0,0.18), transparent 55%), radial-gradient(circle at 80% 70%, rgba(245,181,0,0.10), transparent 60%)' }} />
 <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-10 p-10 lg:p-14 items-center">
 <div>
 <span className="pill-badge"><Users className="w-3.5 h-3.5" /> We&apos;re social</span>
 <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-5">
 Join the <span className="text-amber-brand">discussion</span> online.
 </h2>
 <p className="mt-5 text-background/70 leading-relaxed text-[15px] max-w-md">
 Stay connected between visits — cycle tips on YouTube, batch shots on Instagram,
 live Q&amp;A in the community, and a weekly digest straight to your inbox.
 </p>
 <div className="mt-8 flex flex-wrap items-center gap-3">
 {socialLinks.map(({ icon: Ic, label, href, color }) => (
 <a
 key={label}
 href={href}
 target="_blank"
 rel="noreferrer"
 className={`inline-flex items-center gap-2 rounded-full border border-background/20 bg-background/5 px-4 py-2.5 text-sm font-semibold text-background/90 hover:bg-background/10 transition-colors ${color}`}
 data-testid={`social-${label.toLowerCase()}`}
 >
 <Ic className="w-4 h-4" /> {label}
 </a>
 ))}
 <button
 type="button"
 onClick={() => navigate('/platform/community')}
 className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary text-primary-foreground px-4 py-2.5 text-sm font-extrabold hover:opacity-90 transition-opacity"
 data-testid="social-forum"
 >
 <MessageSquare className="w-4 h-4" /> Community Forum
 </button>
 </div>
 </div>

 {/* Newsletter capture */}
 <div className="rounded-2xl bg-background/5 border border-background/10 p-6 backdrop-blur-sm" data-testid="newsletter-card">
 <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-primary font-extrabold">
 <Mail className="w-3 h-3" /> Weekly digest
 </div>
 <h3 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-background">
 Get the best of the week in 5 minutes.
 </h3>
 <p className="mt-2 text-sm text-background/70 leading-relaxed">
 One email every Friday: new lessons, top forum threads, calculator updates.
 No spam. Unsubscribe any time.
 </p>
 <Button
 size="lg"
 onClick={() => { setAuthMode('register'); setShowAuth(true); }}
 className="mt-5 w-full rounded-full font-extrabold bg-primary text-primary-foreground hover:bg-primary/90"
 data-testid="newsletter-signup-btn"
 >
 Sign up free <ArrowRight className="w-4 h-4 ml-2" />
 </Button>
 <p className="mt-3 text-[11px] text-background/50 text-center leading-relaxed">
 Free tier includes the weekly digest — no credit card needed.
 </p>
 </div>
 </div>
 </div>
 </section>

 {/* Footer */}
 <footer className="bg-navy text-background/70 mt-10">
 <div className="container mx-auto px-6 py-12">
 <div className="grid md:grid-cols-4 gap-8 mb-8">
 <div className="md:col-span-2">
 <div className="flex items-center gap-2.5">
 <img
 src="/brand-logo.png"
 alt="Freeze Dry Academy"
 className="h-9 w-auto object-contain"
 draggable={false}
 />
 <span className="font-heading text-base font-extrabold text-background tracking-tight">Freeze Dry Academy</span>
 </div>
 <p className="mt-3 text-sm leading-relaxed max-w-sm">
 The complete freeze-dry education hub — courses, tools, recipes, and community.
 </p>
 </div>
              <div>
                <div className="eyebrow text-background/50 mb-3">Platform</div>
                <ul className="space-y-2 text-sm">
                  <li><a href="#features" className="hover:text-background">Features</a></li>
                  <li><button onClick={() => navigate('/why-us')} className="hover:text-background">Why us</button></li>
                  <li><button onClick={() => navigate('/pricing')} className="hover:text-background">Pricing</button></li>
                  <li>
                    <a
                      href="https://status.freezedry.academy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-background inline-flex items-center gap-1"
                      data-testid="footer-status-link"
                    >
                      Service status <span aria-hidden>↗</span>
                    </a>
                  </li>
                </ul>
              </div>
 <div>
 <div className="eyebrow text-background/50 mb-3">Account</div>
 <ul className="space-y-2 text-sm">
 <li><button onClick={() => navigate('/login')} className="hover:text-background">Log in</button></li>
 <li><button onClick={() => { setAuthMode('register'); setShowAuth(true); }} className="hover:text-background">Sign up</button></li>
 </ul>
 </div>
 </div>
 <div className="border-t border-background/10 pt-6 text-xs flex flex-col md:flex-row justify-between gap-2">
 <span>© 2025 Freeze Dry Academy. All rights reserved.</span>
 <span>Made with care for serious operators.</span>
 </div>
 </div>
 </footer>
 </div>
 );
};

export default LandingPage;
